<template>
  <section
    id="pnlRadioReviewType"
    class="mb-20">
    <h2
      class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-4"
      id="reviewTypeTitle">
      Single-anonymised or double-anonymised peer review
    </h2>
    <FormRadio
      v-model="peerReviewAnonymisation"
      name="pnlRadioReviewType"
      input-id="rdoTypeReviewTypeSingle"
      labelText="Single-anonymised"
      guidanceMessage="(reviewers' identities are hidden from you but author names and affiliations are visible to them)"
      :value="singleAnonymised"
      @custom-change="onReviewerAnonymisationChanged"
      :label-custom-css-classes="['font-bold']"
    />
    <FormRadio
      data-testid="double-anonymised-peer-review"
      v-model="peerReviewAnonymisation"
      name="pnlRadioReviewType"
      input-id="rdoTypeReviewTypeDouble"
      labelText="Double-anonymised"
      guidanceMessage="(both author and reviewer identities are hidden. Authors are responsible for anonymising the manuscript)"
      :value="doubleAnonymised"
      @custom-change="onReviewerAnonymisationChanged"
      :label-custom-css-classes="['font-bold']"
    />
    <InlineWarningMessage
      v-if="error"
      :errorMessage="error"
      class="mt-4"
      errorTestId="peer-review-anonymisation"
    />
    <p class="mt-4">
      For more information please see  <a
        class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
        href="https://www.rsc.org/getContentAsset/283eac44-aaf2-4e6c-8f4e-db0ee9c43e6c/f4c91d86-ac3e-4675-bdf3-f8325ded9710/double-anonymised-author-checklist2.docx?language=en"
        target="_blank"
        id="lnk-reviewEditorial-checklist"
      >
        checklist</a> and <a
        class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
        href="https://www.rsc.org/journals-books-databases/journal-authors-reviewers/processes-policies/#single-double-anonymised-peer-review"
        target="_blank"
        id="lnk-reviewEditorial-reviewGuide"
      >
        guidelines</a>.
    </p>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'
import { ReviewerAnonymisationType } from '@/types'
import FormRadio from '../ui-components/FormRadio.vue'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'

const { error } = defineProps<{ error?: string }>()
const emit = defineEmits(['clear-error'])

const store = useStore()
const singleAnonymised = ref(ReviewerAnonymisationType.SingleAnonymised)
const doubleAnonymised = ref(ReviewerAnonymisationType.DoubleAnonymised)
const peerReviewAnonymisation = ref(store.state.activeSubmission.peerReviewAnonymisation)

function onReviewerAnonymisationChanged(event: Event) {
  const targetId = (event.target as HTMLElement).id
  peerReviewAnonymisation.value = targetId === 'rdoTypeReviewTypeSingle'
    ? ReviewerAnonymisationType.SingleAnonymised
    : ReviewerAnonymisationType.DoubleAnonymised
  store.dispatch('setReviewerAnonymisation', peerReviewAnonymisation.value)
  emit('clear-error')
}
</script>
